import {useEffect, useMemo, useRef, useState} from "react";
import {useTranslation} from "react-i18next";
import clsx from "clsx";
// import Flatpickr from "flatpickr";
import flatpickr from "flatpickr";
import {useField, useFormikContext} from "formik";
import moment from "moment";
import PropTypes from "prop-types";

import {ICON_NAMES} from "../../utilities/generalConstants.js/generalConstants";
import QuestionItem from "../../utilities/QuestionItem";
import ToolTip from "../../utilities/ToolTip";
import useRoutePermissions, {
  UI_PERMISSION_TYPE,
} from "../adminForms/Ba3aSettings/tabs/PositionsSettings/useRoutePermissions";
import Icon from "../Icon/Icon";

import useInputsTooltip from "./hook/InputsTooltipService";
import {Ukrainian} from "./utils/ukrLocale";

import "flatpickr/dist/flatpickr.min.css";

export const SimpleDateInput = ({label, ...props}) => {
  const [field, meta] = useField(props);

  return (
    <div className="project_input" style={{position: "relative"}}>
      <div className="datePicker_withText">
        <label htmlFor={props.name} style={{width: "auto", marginRight: "5px"}}>
          {label}
        </label>
        <input {...props} {...field} type="date" style={{width: "9em"}} pattern="[0-9]{4}-[0-9]{2}-[0-9]{2}" />
        {
          !props.readOnly && <Icon name={ICON_NAMES.CALENDAR} width={18} height={18} />
          // <img src={calendarIcon} alt="" draggable="false" />
        }
      </div>
      {meta.touched && meta.error ? <div className="error ">{meta.error}</div> : null}
    </div>
  );
};

export const DateInput = ({
  label,
  className = "",
  generalClassName = "",
  questionClassName,
  question,
  labelWrap,
  labelW,
  width,
  flatpikrConfig,
  noFormik = false,
  setNoFormikValue = null,
  noFormikValue,
  disabled = false,
  readOnly = false,
  generalStyle = {},
  required,
  style = {},
  fromCurrentDate = false,
  onDateChange = () => {},
  minDate,
  maxDate,
  enableTime = false,
  enableTimeWithSeconds = false,
  tooltipMessage = null,
  bottomTooltip = false,
  noFunctional = false,
  placeholder = "",
  sizeIcon = 17,
  isRequiredAccented = true,
  defaultCurrentDate = false,
  uiPermissionType = UI_PERMISSION_TYPE.WRITABLE,
  ...props
}) => {
  const [field, meta] = useField(props);
  const inputref = useRef();
  const [date, setDateValue] = useState("");
  const {t, i18n} = useTranslation();
  const [flatpickrInstance, setInstanse] = useState(null);

  const {tooltipShown, onMouseEnter, onMouseLeave} = useInputsTooltip(field);

  const {isDisabledByRoutePermission} = useRoutePermissions({uiPermissionType});

  const {setFieldValue} = useFormikContext();
  const isRequiredMarkHighlighted =
    !field.value && !disabled && !readOnly && !noFunctional && !isDisabledByRoutePermission;

  useEffect(() => {
    const instance = flatpickr(inputref.current, {
      dateFormat: enableTime ? "d.m.Y h:i K" : enableTimeWithSeconds ? "d.m.Y h:i:S K" : "d.m.Y",
      enableTime: enableTime || enableTimeWithSeconds,
      enableSeconds: enableTimeWithSeconds,
      clickOpens: false,
      weekNumbers: true,
      locale: i18n.language === "en" ? "en" : Ukrainian,
      appendTo: document.body,
      position: "auto",
      positionElement: inputref.current || undefined,
      onOpen: function (_selectedDates, _dateStr, instance) {
        //!recalc pos onOpen
        try {
          if (instance) {
            instance.set("positionElement", inputref.current || undefined);
            instance.setPosition && instance.setPosition();
          }
        } catch (e) {
          console.log(e);
        }
        try {
          const cal = instance && instance.calendarContainer;
          if (!cal) return;
          cal.style.right = "auto"; //*reset pos
          const margin = 8;
          const rectangle = cal.getBoundingClientRect();
          const viewportWidth = window.innerWidth || document.documentElement.clientWidth;
          const viewportHeight = window.innerHeight || document.documentElement.clientHeight;
          let left = rectangle.left;
          let top = rectangle.top;
          if (rectangle.right > viewportWidth - margin) left = viewportWidth - rectangle.width - margin;
          if (left < margin) left = margin;
          if (rectangle.bottom > viewportHeight - margin) top = viewportHeight - rectangle.height - margin;
          if (top < margin) top = margin;
          cal.style.left = `${Math.round(left + window.pageXOffset)}px`;
          cal.style.top = `${Math.round(top + window.pageYOffset)}px`;
        } catch (e) {
          console.log(e);
        }
      },
      onChange: function (selectedDates) {
        const newDate = selectedDates?.[0]?.toISOString();
        if (noFormik && setNoFormikValue) {
          setNoFormikValue(newDate);
          return;
        }
        setFieldValue(props.name, newDate);
        // setDateValue(newDate);
      },
      ...flatpikrConfig,
      ...(fromCurrentDate ? {minDate: moment().format("DD-MM-YYYY")} : {}),
      ...(minDate ? {minDate} : {}),
      ...(maxDate ? {maxDate} : {}),
    });

    setInstanse(instance);

    return () => {
      try {
        instance && instance.destroy && instance.destroy();
      } catch (e) {
        console.error(e);
      }
      setInstanse(null);
    };
  }, [minDate, maxDate]);

  const formatDateView = date => {
    if (!date) return "";

    return enableTime
      ? flatpickr.formatDate(new Date(date), "d.m.Y h:i K")
      : enableTimeWithSeconds
      ? flatpickr.formatDate(new Date(date), "d.m.Y h:i:S K")
      : flatpickr.formatDate(new Date(date), "d.m.Y");
  };

  useEffect(() => {
    const newValue = noFormik ? noFormikValue : field?.value;

    if (
      typeof newValue === "string" &&
      newValue !== "" &&
      flatpickrInstance &&
      flatpickrInstance.config &&
      formatDateView(newValue) !== date &&
      date !== formatDateView(field?.value)
    ) {
      const d = formatDateView(newValue);
      setDateValue(d);
      onDateChange(newValue);
      if (d) flatpickrInstance?.setDate(new Date(newValue).toISOString(), false);
    } else if ((!newValue || newValue === "") && flatpickrInstance && flatpickrInstance.config) {
      setDateValue("");
      if (field?.value && field?.value !== "") {
        onDateChange("");
      }
      flatpickrInstance.setDate("", false);
    }
  }, [field?.value, flatpickrInstance, noFormikValue]);

  useEffect(() => {
    if (defaultCurrentDate && !date && setFieldValue) {
      setFieldValue(props.name, new Date().toISOString());
      setDateValue(formatDateView(new Date()));
    }
  }, [defaultCurrentDate, date, props.name, setFieldValue]);

  const inp = useMemo(() => {
    return (
      <input
        placeholder={t(placeholder)}
        className="calendar"
        ref={inputref}
        // {...props}
        // {...field}
        value={date}
        type="text"
        pattern="[0-9]{4}-[0-9]{2}-[0-9]{2}"
        style={{
          width: width ? width : enableTime ? "15em" : "6em",
          cursor: disabled || noFunctional || readOnly ? "auto" : "pointer",
          ...generalStyle,
          ...(readOnly || disabled || noFunctional || isDisabledByRoutePermission
            ? {cursor: "default"}
            : {background: "white"}),
        }}
        readOnly={true} // Отключение возможности ввода текста, так как Flatpickr будет отвечать за выбор даты
        onMouseEnter={question ? onMouseEnter : null}
        onMouseLeave={question ? onMouseLeave : null}
      />
    );
  }, [date, readOnly, disabled, isDisabledByRoutePermission]);

  const labelText = label ? t(label) : "";

  return (
    <ToolTip
      className={`project_input ${labelWrap ? "" : "labelWrap"} ${generalClassName}`}
      style={{position: "relative", ...style}}
      title={
        noFunctional
          ? "No functional"
          : isDisabledByRoutePermission
          ? "Your position has no permission for this action"
          : tooltipMessage
      }
      bottom={bottomTooltip}
    >
      <>
        <div
          className={clsx("datePicker_withText gap5 row", className, !readOnly && "aic", readOnly && "aic")}
          onClick={() => {
            if (disabled || readOnly || noFunctional || isDisabledByRoutePermission) return;
            if (flatpickrInstance) {
              try {
                flatpickrInstance.set("positionElement", inputref.current || undefined);
                flatpickrInstance.setPosition && flatpickrInstance.setPosition();
              } catch (e) {
                console.log(e);
              }
              flatpickrInstance.open();
            }
          }}
        >
          {label && (
            <label htmlFor={props.name} style={{width: labelW || "auto"}} className={"autolabel fdr aic"}>
              <span>
                {labelText}
                {required && (
                  <span className={isRequiredAccented && isRequiredMarkHighlighted ? "required-asterisk" : ""}>*</span>
                )}
                :
              </span>
            </label>
          )}
          <div className="row aife">
            {inp}
            {!readOnly && !isDisabledByRoutePermission && !noFunctional && (
              <Icon
                name={ICON_NAMES.CALENDAR}
                style={{
                  cursor: disabled || noFunctional || readOnly || isDisabledByRoutePermission ? "auto" : "pointer",
                }}
                width={sizeIcon}
                height={sizeIcon}
              />
            )}
          </div>
          {question ? (
            <div className={clsx("question-date", questionClassName)}>
              <QuestionItem title={question} tooltipShown={tooltipShown} />
            </div>
          ) : null}
        </div>
        {meta.touched && meta.error ? <div className="error error_dateInp">{meta.error}</div> : null}
      </>
    </ToolTip>
  );
};

DateInput.propTypes = {
  label: PropTypes.oneOfType([PropTypes.string, PropTypes.element, PropTypes.node]),
  className: PropTypes.string,
  disabled: PropTypes.bool,
  props: PropTypes.array,
  noFunctional: PropTypes.bool,
};

export default DateInput;
